import { flushSync } from 'react-dom'
import type { NavigateFunction } from 'react-router-dom'
import { useAuthStore } from '@/store/useAuthStore'
import {
  applyAuthSessionToClient,
  fetchAndHydrateAuthSession,
  type AuthSessionPayload,
} from '@/lib/auth/hydrateFromAuthSession'
import {
  applyAuthRole,
  navigateToAuthRedirect,
  resolvePostAuthRedirect,
  resolveSessionNavigationRedirect,
  runPostAuthSideEffects,
  type AuthRedirectPayload,
  type InvitationRedirectInfo,
} from '@/lib/auth/postAuthRedirect'
import { markSessionBootstrapComplete } from '@/utils/bootstrapAppSession'
import { clearSessionSwapQueries } from '@/utils/queryHelpers'

export class FinalizeSessionError extends Error {
  readonly code: string

  constructor(code: string, message?: string) {
    super(message ?? code)
    this.name = 'FinalizeSessionError'
    this.code = code
  }
}

/** Purge React Query : tout (login) ou tout sauf super-admin (impersonation). */
export type SessionTransitionMode = 'none' | 'full' | 'scoped'

export interface BeginSessionTransitionOptions {
  mode: SessionTransitionMode
  suppressToasts?: boolean
  /** Swap session sans AppLoadingScreen (impersonation). */
  silent?: boolean
  /** Start impersonation depuis super-admin — évite skeleton liste pendant l'API. */
  preserveSuperAdminData?: boolean
  /** Purge RQ après navigate (stop impersonation — évite skeleton page team). */
  deferCacheClear?: boolean
}

/** Annule les requêtes en vol et purge le contexte équipe avant un changement d'utilisateur. */
export async function beginSessionTransition(
  options: BeginSessionTransitionOptions
): Promise<void> {
  if (options.mode === 'none') {
    return
  }

  const { queryClient } = await import('@/lib/react-query')
  const { useSessionUiStore } = await import('@/store/useSessionUiStore')

  if (options.suppressToasts) {
    const { setSuppressToasts } = await import('@/utils/messageClient')
    setSuppressToasts(true)
  }

  if (options.silent) {
    flushSync(() => {
      useAuthStore.setState({ sessionSilentSwap: true })
    })
  } else if (options.mode === 'scoped' || options.suppressToasts) {
    flushSync(() => {
      useAuthStore.setState({ sessionTransitioning: true })
    })
  }

  await queryClient.cancelQueries()

  if (options.mode === 'full') {
    queryClient.removeQueries()
    useAuthStore.setState({
      activeTeamId: null,
      activeTeamRole: null,
      sessionHydrated: false,
      impersonating: false,
    })
  } else if (options.mode === 'scoped') {
    const { resetSidebarPrefetchCache } = await import('@/lib/navigation/prefetchSidebarRoute')
    resetSidebarPrefetchCache()
    if (!options.deferCacheClear) {
      clearSessionSwapQueries({
        preserveSuperAdminData: options.preserveSuperAdminData,
      })
    }
    useAuthStore.setState({
      activeTeamId: null,
      activeTeamRole: null,
    })
  }

  useSessionUiStore.getState().clear()
}

export function endSessionTransition(options?: { suppressToasts?: boolean }): void {
  flushSync(() => {
    useAuthStore.setState({ sessionTransitioning: false, sessionSilentSwap: false })
  })

  if (options?.suppressToasts) {
    void import('@/utils/messageClient').then(({ setSuppressToasts }) => {
      window.setTimeout(() => setSuppressToasts(false), 400)
    })
  }
}

/** Applique la session inline, ou réutilise le store, ou fetch GET /auth/session. */
export async function hydrateSessionAfterAuth(
  sessionFromResponse?: AuthSessionPayload | null,
  options?: { forceRefetch?: boolean }
): Promise<boolean> {
  if (sessionFromResponse?.user?.id) {
    applyAuthSessionToClient(sessionFromResponse)
    return true
  }

  if (!options?.forceRefetch) {
    const { sessionHydrated, user } = useAuthStore.getState()
    if (sessionHydrated && user?.id) {
      return true
    }
  }

  return fetchAndHydrateAuthSession()
}

export interface FinalizeAuthenticatedSessionParams {
  navigate?: NavigateFunction
  session?: AuthSessionPayload | null
  redirect?: AuthRedirectPayload | null
  redirectUrl?: string | null
  invitation?: InvitationRedirectInfo | null
  role?: string | null
  /** Marque le bootstrap React Query terminé (défaut: true). */
  markBootstrapComplete?: boolean
  /** PostHog + initActiveTeam si besoin (défaut: true). */
  runSideEffects?: boolean
  /** Re-fetch GET /auth/session (post-onboarding : required_path obsolète en store). */
  forceRefetch?: boolean
  /** Purge React Query avant hydrate (`full` login/signup, `scoped` impersonation). */
  transition?: SessionTransitionMode
  /** Navigation synchrone avant hydrate (stop impersonation — évite 403 sur pages team). */
  preHydrateRedirect?: AuthRedirectPayload | null
  /** Redirige via sessionRequiredPath / sessionDefaultPath (impersonation). */
  useSessionRedirect?: boolean
  /** Navigation replace synchrone (flushSync) après hydrate. */
  syncNavigate?: boolean
  /** Masque les toasts pendant la transition (impersonation). */
  suppressToasts?: boolean
  /** Force le flag impersonating après hydrate. */
  impersonating?: boolean
  /** Termine sessionTransitioning en finally (défaut: true si transition !== none). */
  endTransition?: boolean
  /** Purge RQ après preHydrateRedirect (stop impersonation). */
  clearSessionCacheAfterRedirect?: boolean
  preserveSuperAdminCache?: boolean
}

export interface FinalizeAuthenticatedSessionResult {
  redirect: AuthRedirectPayload
  effectiveRole: string | null
}

function navigateAfterFinalize(
  navigate: NavigateFunction,
  redirect: AuthRedirectPayload,
  sync: boolean
): void {
  if (sync) {
    flushSync(() => {
      navigateToAuthRedirect(navigate, redirect)
    })
    return
  }

  navigateToAuthRedirect(navigate, redirect)
}

/**
 * Writer session unique : login, signup, onboarding, complete-registration, impersonation.
 */
export async function finalizeAuthenticatedSession(
  params: FinalizeAuthenticatedSessionParams
): Promise<FinalizeAuthenticatedSessionResult> {
  const transitionMode = params.transition ?? 'none'
  const shouldEndTransition =
    params.endTransition ?? (transitionMode !== 'none' && (transitionMode === 'scoped' || Boolean(params.suppressToasts)))
  let transitionStarted = false

  try {
    if (transitionMode !== 'none') {
      await beginSessionTransition({
        mode: transitionMode,
        suppressToasts: params.suppressToasts,
      })
      transitionStarted = true
    }

    if (params.preHydrateRedirect && params.navigate) {
      flushSync(() => {
        if (params.impersonating === false) {
          useAuthStore.setState({ impersonating: false })
        }
        navigateToAuthRedirect(params.navigate!, params.preHydrateRedirect!)
      })

      if (params.clearSessionCacheAfterRedirect) {
        clearSessionSwapQueries({
          preserveSuperAdminData: params.preserveSuperAdminCache,
        })
      }
    }

    // Nouvelle session explicite (login) : ne pas réutiliser activeTeamId d'un autre compte
    if (params.session?.user?.id) {
      useAuthStore.setState({ activeTeamId: null, activeTeamRole: null })
    }

    const sessionHydrated = await hydrateSessionAfterAuth(params.session, {
      forceRefetch: params.forceRefetch,
    })
    if (!sessionHydrated) {
      throw new FinalizeSessionError('SESSION_HYDRATE_FAILED')
    }

    if (params.impersonating !== undefined) {
      useAuthStore.setState({ impersonating: params.impersonating })
    }

    if (params.markBootstrapComplete !== false) {
      markSessionBootstrapComplete()
    }

    useAuthStore.setState({
      sessionBootstrapped: true,
      sessionHydrated: true,
    })

    const { globalRole } = useAuthStore.getState()
    const effectiveRole = params.role ?? globalRole
    applyAuthRole(effectiveRole)

    const redirect = params.preHydrateRedirect
      ? params.preHydrateRedirect
      : params.useSessionRedirect
        ? resolveSessionNavigationRedirect()
        : await resolvePostAuthRedirect({
            redirect: params.redirect ?? null,
            redirectUrl: params.redirectUrl,
            invitation: params.invitation,
          })

    if (params.navigate && !params.preHydrateRedirect) {
      navigateAfterFinalize(params.navigate, redirect, Boolean(params.syncNavigate))
    }

    if (params.navigate && effectiveRole) {
      const { activeTeamId } = useAuthStore.getState()
      void import('@/lib/navigation/prefetchSidebarRoute').then(({ prefetchSidebarRoute }) =>
        prefetchSidebarRoute(
          { path: redirect.path, role: effectiveRole, activeTeamId },
          { ignoreApiReady: true }
        )
      )
    }

    if (params.runSideEffects !== false) {
      runPostAuthSideEffects(effectiveRole, { invalidateQueries: false })
    }

    return { redirect, effectiveRole }
  } catch (error) {
    if (transitionMode === 'scoped') {
      useAuthStore.setState({ impersonating: false })
    }
    throw error
  } finally {
    if (transitionStarted && shouldEndTransition) {
      endSessionTransition({ suppressToasts: params.suppressToasts })
    }
  }
}
